// What each step runs on. The trigger decides it for the steps at the root (a
// signal, or a placed prospect order); a step that makes a new subject ("Create
// prospect order" on a signal) runs the steps chained under it on that. Which
// steps and agents may go where follows from it, as the API decides it.

import { createContext } from "react";
import {
  SIGNAL_TRIGGER,
  type WorkflowStepType,
  type WorkflowSubjectKind,
  type WorkflowTriggerType,
} from "../../../api/client.ts";
import { triggerDataOf } from "./graph.ts";
import {
  STEP_SUBJECTS,
  TRIGGER_ID,
  CUSTOM_TRIGGER,
  apiStepType,
  childSubjectKind,
  type ActionData,
  type ActionKind,
  type TriggerData,
  type ConditionEdge,
  type StepData,
  type StepNode,
} from "./types.ts";

/** The kind of subject every connected step runs on, by node id (the trigger: its own). */
export function subjectsOf(
  nodes: StepNode[],
  edges: ConditionEdge[],
  // What a custom step makes for the steps under it; the step types endpoint's.
  stepTypes?: WorkflowStepType[],
  // What a custom trigger's steps run on; the trigger types endpoint's.
  triggerTypes?: WorkflowTriggerType[],
): Map<string, WorkflowSubjectKind> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, string[]>();
  for (const e of edges) outgoing.set(e.source, [...(outgoing.get(e.source) ?? []), e.target]);
  const subjects = new Map<string, WorkflowSubjectKind>([
    [TRIGGER_ID, triggerSubject(triggerDataOf(nodes), triggerTypes)],
  ]);
  const queue = [TRIGGER_ID];
  while (queue.length) {
    const id = queue.shift()!;
    const node = byId.get(id);
    const below = (node && childSubjectKind(node.data, stepTypes)) ?? subjects.get(id)!;
    for (const child of outgoing.get(id) ?? []) {
      if (subjects.has(child)) continue;
      subjects.set(child, below);
      queue.push(child);
    }
  }
  return subjects;
}

/** What the steps at the root run on: the trigger's own type, or what a custom trigger declares. */
export function triggerSubject(
  trigger: TriggerData | undefined,
  triggerTypes?: WorkflowTriggerType[],
): WorkflowSubjectKind {
  if (!trigger) return SIGNAL_TRIGGER;
  if (trigger.trigger_type !== CUSTOM_TRIGGER) return trigger.trigger_type;
  const type = triggerTypes?.find((t) => t.slug === trigger.custom_type);
  return type?.subject_kind ?? trigger.custom_type;
}

/** The kind of subject the steps chained under `node` run on. */
export function childSubjectOf(
  node: StepNode,
  subjects: Map<string, WorkflowSubjectKind>,
  stepTypes?: WorkflowStepType[],
): WorkflowSubjectKind {
  return childSubjectKind(node.data, stepTypes) ?? subjects.get(node.id) ?? SIGNAL_TRIGGER;
}

/** Whether `data`'s step makes a new subject for the steps under it. */
export function createsSubject(data: StepData | undefined, stepTypes?: WorkflowStepType[]): boolean {
  return !!data && childSubjectKind(data, stepTypes) !== undefined;
}

/** A step first on its subject: right under the trigger, or under the step that made it. */
export function isFirstOnSubject(
  parentId: string | undefined,
  parent: StepData | undefined,
  stepTypes?: WorkflowStepType[],
): boolean {
  return parentId === TRIGGER_ID || createsSubject(parent, stepTypes);
}

/** Which subjects a step runs on: the step types endpoint's, else the local table. */
export function stepSubjects(step: ActionKind | ActionData, stepTypes?: WorkflowStepType[]): WorkflowSubjectKind[] {
  const kind = typeof step === "string" ? step : step.kind;
  const slug = typeof step === "string" ? step : apiStepType(step);
  return stepTypes?.find((t) => t.slug === slug)?.subject_kinds ?? STEP_SUBJECTS[kind] ?? [];
}

// The trigger and step types as the endpoints describe them, for the canvas
// and the panel: labels and settings of the types the editor has no form for.
export interface WorkflowTypes {
  stepTypes?: WorkflowStepType[];
  triggerTypes?: WorkflowTriggerType[];
}

export const WorkflowTypesContext = createContext<WorkflowTypes>({});

// `subjectsOf` the editor's graph, for the canvas: a step on another subject
// than the trigger's (the order a step made) says so on its card.
export const SubjectsContext = createContext<Map<string, WorkflowSubjectKind>>(new Map());
